import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from './lib/react-router-dom';
import { useTenant, getTenantConfig } from './config/tenantConfig';
import { getHostname, hostMatchesList, toDomainList } from './config/domainUtils';
import { useAuth } from './context/AuthContext';
import { RoleProvider } from './auth/RoleContext';
import { usePermissions } from './auth/usePermissions';
import PermissionGuard from './components/ProtectedRoute';
import { SIDEBAR_ITEM_KEYS, isPlatformAdmin } from './auth/permissionsConfig';
import { getUserDashboardRoute } from './lib/userDashboardRoute';

// Layouts can be loaded statically as they are part of the main shell
import CustomerLayout from './layouts/CustomerLayout';
import PlatformLayout from './layouts/PlatformLayout';
import ReferralLayout from './layouts/ReferralLayout';
import HaliTradeLayout from './layouts/HaliTradeLayout';
import HaliLabLayout from './layouts/HaliLabLayout';
import InvestorLayout from './layouts/InvestorLayout';

// --- Lazy Loaded Pages ---

// Customer Pages
const CustomerDashboard = lazy(() => import('./Customer/Dashboard'));
const CustomerWallet = lazy(() => import('./Customer/CustomerWallet'));
const ValidationStatus = lazy(() => import('./Customer/ValidationStatus'));
const CustomerAgent = lazy(() => import('./components/customer-agent/CustomerAgent.jsx'));
const InvoiceFinancingGuideAgent = lazy(() => import('./components/invoice-financing-agent/InvoiceFinancingGuideAgent.jsx'));
const CustomerInvoiceFinancing = lazy(() => import('./Customer/InvoiceFinancing.jsx'));
const InvoiceSubmissionPage = lazy(() => import('./Customer/InvoiceSubmissionPage.jsx'));
const TradePartnerInvoicesPage = lazy(() => import('./Customer/TradePartnerInvoicesPage.jsx'));
const CustomerMessages = lazy(() => import('./Customer/Messages.jsx'));
const MyOnboardingSubmissions = lazy(() => import('./Customer/MyOnboardingSubmissions.jsx'));
const MyContracts = lazy(() => import('./Customer/MyContracts.jsx'));
const MyTermsheets = lazy(() => import('./Customer/MyTermsheets.jsx'));
const MyCompanies = lazy(() => import('./Customer/MyCompanies.jsx'));
const HaliDocPage = lazy(() => import('./termsheet_module/HaliDocPage.jsx'));
const NOADetailPage = lazy(() => import('./pages/NOADetailPage.jsx'));
const CompanyDetailPage = lazy(() => import('./pages/CompanyModule'));
const TradePartnerDetailPage = lazy(() => import('./pages/TradePartnerModule'));

// Platform Pages
const PlatformDashboard = lazy(() => import('./Platform/Dashboard'));
const PlatformDashboardNew = lazy(() => import('./Platform/DashboardNew')); // NEW PROFESSIONAL DASHBOARD
const ValidationQueue = lazy(() => import('./Platform/ValidationQueue'));
const ReferralManagement = lazy(() => import('./Platform/ReferralManagement'));
const CustomersManagement = lazy(() => import('./Platform/CustomersManagement'));
const UserManagementModule = lazy(() => import('./UserManagement/UserManagementModule'));
const PlatformFactorTools = lazy(() => import('./Platform/FactorTools'));
const FactorToolsAgent = lazy(() => import('./FactorTools')); // New Factor Financial Agent
const HaliDocAgentPage = lazy(() => import('./HaliDocAgentPage')); // Hali Doc Agent standalone page
const HaliLeadsAgentPage = lazy(() => import('./pages/HaliLeadsAgentPage'));
const EvaChatPage = lazy(() => import('./pages/EvaChatPage')); // Hali Leads Agent demo page
const LeadsPage = lazy(() => import('./pages/LeadsPage')); // Leads page with header
const CreditInsurancePage = lazy(() => import('./pages/CreditInsurancePage')); // Credit insurance cockpit (platform)
const LeadDashboard = lazy(() => import('./components/leads/LeadDashboard'));
const LeadDetail = lazy(() => import('./components/leads/LeadDetail'));
const LeadForm = lazy(() => import('./components/leads/LeadForm'));
const KYCAgentTest = lazy(() => import('./factoring/KYCAgentTest'));
const KycAIAgent = lazy(() => import('./factoring/KycAIAgent'));
const BankStatementPage = lazy(() => import('./factoring/BankStatementPage'));
const PastTradeAgent = lazy(() => import('./factoring/PastTradeAgent'));
const AnalyticsHub = lazy(() => import('./Platform/AnalyticsHub.jsx'));
const LegalOfficer = lazy(() => import('./Platform/LegalOfficer.jsx'));
const CustomerChatSupport = lazy(() => import('./Platform/CustomerChatSupport'));
const SentTermsheets = lazy(() => import('./Platform/SentTermsheets'));
const PlatformFinancingDashboard = lazy(() => import('./Platform/FinancingDashboard'));
const PlatformMarketplaceDashboard = lazy(() => import('./Platform/MarketplaceDashboard'));
const WalletDashboard = lazy(() => import('./Platform/WalletDashboard'));
const RiskValidationDashboard = lazy(() => import('./Platform/RiskValidationDashboard'));
const WithdrawalRequest = lazy(() => import('./Platform/WithdrawalRequest'));
const FinanceTeamDashboard = lazy(() => import('./Platform/FinanceTeamDashboard'));
const ApprovalQueue = lazy(() => import('./Platform/ApprovalQueue'));
const TransactionMonitor = lazy(() => import('./Platform/TransactionMonitor'));
const InvoiceFinancingDetail = lazy(() => import('./Platform/InvoiceFinancingDetail'));
const ComingSoon = lazy(() => import('./components/ComingSoon'));
const AllocationDetailPage = lazy(() => import('./Platform/AllocationDetailPage'));
const WalletDetail = lazy(() => import('./Platform/WalletDetail'));
const OnboardingSubmissions = lazy(() => import('./Platform/OnboardingSubmissions'));
const WalletApprovals = lazy(() => import('./Platform/WalletApprovals'));
const ProductPricingAdmin = lazy(() => import('./Platform/ProductPricingAdmin.jsx'));
const PlatformRiskMatrixPage = lazy(() => import('./Platform/PlatformRiskMatrixPage.jsx'));
const RepaymentPricingSettings = lazy(() => import('./Platform/RepaymentPricingSettings.jsx'));

// HaliPass Module (Onboarding & KYC)
const HaliPassDashboard = lazy(() => import('./modules/HaliPass/HaliPassDashboard'));

// HaliBlog Module (AI-generated Trade Finance content)
const HaliBlog = lazy(() => import('./components/HaliBlog/HaliBlog'));
const HaliBlogPost = lazy(() => import('./components/HaliBlog/HaliBlogPost'));
const HaliBlogSettings = lazy(() => import('./components/HaliBlog/HaliBlogSettings'));

// LC Trade Document Check (Zero-Fault LC Verification)
const LCTradeDocCheck = lazy(() => import('./components/LCTradeDocCheck'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

const WhiteLabelARPAWithOIF = lazy(() => import('./modules/ARPA/WhiteLabelARPAWithOIF'));

// Referral Pages
const ReferralDashboard = lazy(() => import('./Referral/Dashboard')); // My Opportunities (legacy list)
const ReferralDashboardNew = lazy(() => import('./Referral/ReferralDashboardNew')); // Default dashboard
const ReferralSubmissionForm = lazy(() => import('./Referral/ReferralSubmissionForm'));
const AgentSettingsPage = lazy(() => import('./pages/AgentSettingsPage'));

// Investor Pages
const InvestorDashboard = lazy(() => import('./Investor/InvestorDashboard'));
const InvestorInvoiceDetail = lazy(() => import('./Investor/InvestorInvoiceDetail'));
// Use shared MarketplaceDashboard with tenantType prop
const InvestorMarketplace = lazy(() => import('./Platform/MarketplaceDashboard'));
const InvestorPortfolio = lazy(() => import('./Investor/Portfolio'));
const InvestorPortfolioNew = lazy(() => import('./Investor/PortfolioNew')); // NEW PROFESSIONAL PORTFOLIO
const InvestorDashboardPreview = lazy(() => import('./pages/InvestorDashboardPage')); // v0.1-preview, real data
const InvestorSettings = lazy(() => import('./Investor/Settings'));
const InvestorRiskMatrix = lazy(() => import('./Investor/RiskMatrixPage'));

// Demo/sandbox accounts land on the rich, real-data dashboard (NAV chart +
// maturity ladder, populated by the demo seed). Real investors keep the
// current default dashboard — this switch is additive and demo-scoped.
function InvestorDashboardSwitch() {
  const { user } = useAuth();
  return user?.is_demo ? <InvestorDashboardPreview /> : <InvestorDashboard />;
}

// Shared E-Wallet Component (used by all tenants)
const EWallet = lazy(() => import('./components/EWallet'));
const EWalletNew = lazy(() => import('./components/EWalletNew')); // NEW PROFESSIONAL WALLET
const WalletDepositPage = lazy(() => import('./pages/WalletDepositPage'));
const WalletWithdrawPage = lazy(() => import('./pages/WalletWithdrawPage'));
const TransactionDetailPage = lazy(() => import('./pages/TransactionDetailPage'));
const DepositDetailPage = lazy(() => import('./pages/DepositDetailPage'));
const WithdrawalDetailPage = lazy(() => import('./pages/WithdrawalDetailPage'));

// Buyer Pages
const BuyerDashboard = lazy(() => import('./Buyer/BuyerDashboard'));
const BuyerInvoiceDetail = lazy(() => import('./Buyer/BuyerInvoiceDetail'));

// ... (keep other imports)

const InvestorRoutes = ({ basePath }) => (
  <InvestorLayout>
    <Routes>
      <Route path="dashboard" element={<InvestorDashboardSwitch />} />
      <Route path="dashboard-preview" element={<InvestorDashboardPreview />} />
      <Route path="marketplace" element={<InvestorMarketplace tenantType="investor" />} />
      <Route path="opportunity/:id" element={<InvestorInvoiceDetail />} />
      <Route path="portfolio" element={<InvestorPortfolio />} />
      <Route path="portfolio-new" element={<InvestorPortfolioNew />} />
      <Route path="wallet" element={<EWalletNew userType="investor" />} />
      <Route path="wallet-old" element={<EWallet userType="investor" />} />
      <Route path="wallet/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="settings" element={<InvestorSettings />} />
      <Route path="risk-matrix" element={<InvestorRiskMatrix />} />
      <Route path="companies/:companyId" element={<CompanyModule />} />
      <Route path="company" element={<CompanyModule />} />
      <Route path="/" element={<Navigate to={`${basePath}/dashboard`} replace />} />
      <Route path="*" element={<Navigate to={`${basePath}/dashboard`} replace />} />
    </Routes>
  </InvestorLayout>
);

const HalifundRoutes = () => <InvestorRoutes basePath="/halifund" />;
const InvestorLegacyRoutes = () => <InvestorRoutes basePath="/investor" />;

const BuyerRoutes = () => (
  <CustomerLayout>
    <Routes>
      <Route path="dashboard" element={<BuyerDashboard />} />
      <Route path="invoice/:id" element={<BuyerInvoiceDetail />} />
      <Route path="invoice-financing" element={<CustomerInvoiceFinancing />} />
      <Route path="invoice-financing/:id" element={<InvoiceFinancingDetail />} />
      <Route path="wallet" element={<EWalletNew userType="customer" />} />
      <Route path="wallet-old" element={<EWallet userType="buyer" />} />
      <Route path="wallet/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="/" element={<Navigate to="/buyer/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/buyer/dashboard" replace />} />
    </Routes>
  </CustomerLayout>
);

// Shared Pages
const CompanyModule = lazy(() => import('./CompanyModule'));
const TradePartnerModule = lazy(() => import('./TradePartnerModule'));
const AuthContainer = lazy(() => import('./components/Auth/AuthContainer'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const FinancialAuditAnalyzer = lazy(() => import('./Invoice/FinancialAuditAnalyzer.jsx'));
const HaliSoftLanding = lazy(() => import('./landing/HaliSoftLanding.jsx'));
const HaliSoftPricing = lazy(() => import('./landing/HaliSoftPricing.jsx'));
const SubscriptionPricing = lazy(() => import('./components/subscription/PricingPage.jsx'));
const HaliSoftAbout = lazy(() => import('./landing/HaliSoftAbout.jsx'));
const LoicLanding = lazy(() => import('./landing/LoicLanding.jsx'));
const HaliSoftServices = lazy(() => import('./landing/HaliSoftServices.jsx'));
const HaliSoftContact = lazy(() => import('./landing/HaliSoftContact.jsx'));
const HaliSoftProducts = lazy(() => import('./landing/HaliSoftProducts.jsx'));
const ProductBrochure = lazy(() => import('./landing/ProductBrochure.jsx'));
const AIToolBrochure = lazy(() => import('./landing/AIToolBrochure.jsx'));
const HaliSoftCalendly = lazy(() => import('./landing/HaliSoftCalendly.jsx'));
const HaliTradeLanding = lazy(() => import('./landing/HaliTradeLanding.jsx'));
const HaliTradePricing = lazy(() => import('./landing/HaliTradePricing.jsx'));
const HaliTradeContact = lazy(() => import('./landing/HaliTradeContact.jsx'));
const HaliFlowLanding = lazy(() => import('./landing/HaliFlowLanding.jsx'));
// New restructured landing pages
const HaliSoftAITools = lazy(() => import('./landing/pages/halisoft/HaliSoftAITools.jsx'));
const HaliSoftFAQ = lazy(() => import('./landing/pages/halisoft/HaliSoftFAQ.jsx'));
const HaliTradeAITools = lazy(() => import('./landing/pages/halitrade/HaliTradeAITools.jsx'));
const HaliTradeFAQ = lazy(() => import('./landing/pages/halitrade/HaliTradeFAQ.jsx'));
const HaliAgentFAQ = lazy(() => import('./landing/pages/haliagent/HaliAgentFAQ.jsx'));
const InvoicePlatformPage = lazy(() => import('./landing/InvoicePlatformPage.jsx'));
const InvoiceOCRTab = lazy(() => import('./Trade Partners/InvoiceOCRTab'));
const FinancialAnalysis = lazy(() => import('./factoring/FinancialAnalysis.jsx'));
const FinancialAgent = lazy(() => import('./factoring/FinancialAgent.jsx'));
const ContractAnalyzerModern = lazy(() => import('./factoring/ContractAnalyzerModern.jsx'));
const ContractAgent = lazy(() => import('./factoring/ContractAgent.jsx'));
const FinancialAgentStudio = lazy(() => import('./factoring/FinancialAgentStudio.jsx'));
const InvoiceFinancing = lazy(() => import('./invoiceFinancing/InvoiceFinancing'));
const InvoiceFinancingCheck = lazy(() => import('./invoiceFinancing/InvoiceFinancingHaliCheck'));
const CustomerFinancingDashboard = lazy(() => import('./invoiceFinancing/CustomerDashboard'));
const TradeDocumentRiskAnalyzer = lazy(() => import('./factoring/TradeDocumentRiskAnalyzer.jsx'));
const PastTradeStudio = TradeDocumentRiskAnalyzer; // PastTradeStudio is now TradeDocumentRiskAnalyzer
const TradeDocumentRiskAnalyzerGate = lazy(() => import('./components/TradeDocumentRiskAnalyzerGate.jsx'));
const RiskAnalysis = lazy(() => import('./factoring/RiskAnalysis.jsx'));
const FinancialDocumentAnalyzer = lazy(() => import('./factoring/FinancialDocumentAnalyzer.jsx'));
const BLAnalyzer = lazy(() => import('./factoring/BLAnalyzer.jsx'));
const InvoiceRiskAnalyzer = lazy(() => import('./factoring/InvoiceRiskAnalyzer.jsx'));
const AgentKYC = lazy(() => import('./factoring/AgentKYC.jsx'));
const HaliTradeInvoiceOnboarding = lazy(() => import('./Invoice/HaliTradeInvoiceOnboarding.jsx'));
const OnboardingStudio = lazy(() => import('./factoring/OnboardingStudio.jsx'));
const InvoiceOnboardingSystem = lazy(() => import('./Invoice/InvoiceOnboardingSystem.jsx'));
const SalesTermsheet = lazy(() => import('./sales_termsheet_report/termsheets-dashboard-final.jsx'));
const SalesOwnerDetailPage = lazy(() => import('./sales_termsheet_report/SalesOwnerDetailPage.jsx'));
const TermsheetDetailPage = lazy(() => import('./sales_termsheet_report/TermsheetDetailPage.jsx'));
const MonthlyKPIDetailPage = lazy(() => import('./sales_termsheet_report/MonthlyKPIDetailPage.jsx'));
const FinancedDetailPage = lazy(() => import('./financed_report/FinancedDetailPage.jsx'));
const CreditLimitDashboard = lazy(() => import('./credit_limit_report/CreditLimitDashboard.jsx'));
const FinancedDashboard = lazy(() => import('./financed_report/FinancedDashboard.jsx'));
const AumSalesProductDashboard = lazy(() => import('./AUM_report/AumSalesProductDashboard.jsx'));
const OpportunitiesDashboard = lazy(() => import('./opportunities_report/OpportunitiesDashboard.jsx'));
const SignUpDashboard = lazy(() => import('./sign_up_report/SignUpDashboard.jsx'));
const OnboardingChoice = lazy(() => import('./OnboardingChoice.jsx'));
const RelationshipMap = lazy(() => import('./Trade Partners/RelationshipMap/RelationshipMap.jsx'));
const HaliRiskListPage = lazy(() => import('./halitrade/HaliRiskListPage.jsx'));
const HaliRiskPlaybookDashboard = lazy(() => import('./halirisk/HaliRiskPlaybookDashboard.jsx'));
const HaliRiskV2Page = lazy(() => import('./halirisk-v2/pages/HaliRiskV2Page.jsx'));
const DataExplorer = lazy(() => import('./admin/DataExplorer.jsx'));
const AutoClaudeDashboard = lazy(() => import('./components/AutoClaude/AutoClaudeDashboard.jsx'));
const AutoClaudeTaskDetail = lazy(() => import('./components/AutoClaude/AutoClaudeTaskDetail.jsx'));
const HaliTradeDemo = lazy(() => import('./HaliTradeDemo.jsx'));
const HaliTradeDashboard = lazy(() => import('./halitrade/HaliTradeDashboard.jsx'));
const AgentDashboardNew = lazy(() => import('./halitrade/AgentDashboardNew.jsx')); // NEW AGENT DESIGN TEST
const HaliTradeDebugConsole = lazy(() => import('./halitrade/HaliTradeDebugConsole.jsx'));
const TermsheetPage = lazy(() => import('./halitrade/TermsheetPage.jsx'));
const HaliFlowTermsheetPage = lazy(() => import('./halitrade/TermsheetPage.jsx'));
const OpportunityDetailPage = lazy(() => import('./halitrade/OpportunityDetailPage.jsx'));
const PlatformListPage = lazy(() => import('./halitrade/PlatformListPage.jsx'));
const SettingsPermissions = lazy(() => import('./pages/SettingsPermissions'));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'));
const ContactDetailPage = lazy(() => import('./pages/ContactDetailPage'));
const ObjectPermissions = lazy(() => import('./Platform/ObjectPermissions'));
const HaliTradeAuth = lazy(() => import('./halitrade/HaliTradeAuth.jsx'));
const HaliLabDashboard = lazy(() => import('./halilab/HaliLabDashboard.jsx'));
const HaliLabAuth = lazy(() => import('./halilab/HaliLabAuth.jsx'));
const HaliFinAgent = lazy(() => import('./halilab/HaliFinAgent.jsx'));
const HaliVerify = lazy(() => import('./halilab/HaliVerify.jsx'));
const HaliHubRiskAgent = lazy(() => import('./halihub/HaliHubRiskAgent.jsx'));
const HaliRiskAgent = lazy(() => import('./halihub/HaliHubRiskAgent.jsx')); // Using full version with quota
const HaliContractAgent = lazy(() => import('./halilab/HaliContractAgent.jsx'));
const ModernAuthModule = lazy(() => import('./SignUp/ModernAuthModule'));
const ModernAuthModuleV2 = lazy(() => import('./SignUp/ModernAuthModuleV2'));
const InvoiceSubmissionForm = lazy(() => import('./halilab/prototypes/InvoiceSubmissionForm.jsx'));
const OnboardingStudioHaliTools = lazy(() => import('./Invoice/OnboardingStudioHaliTools.jsx'));
const TradePartnerConfirmation = lazy(() => import('./Trade Partners/TradePartnerConfirmation'));
const BuyerMagicLinkPage = lazy(() => import('./pages/BuyerMagicLinkPage.jsx'));
const AutoHaliAgentEntry = lazy(() => import('./components/AutoHaliAgentEntry.jsx'));
const OnboardingPortal = lazy(() => import('./onboarding-portal/OnboardingPortal.jsx'));
const HaliFormMarketing = lazy(() => import('./haliform/pages/HaliFormMarketing'));
const HaliFormGate = lazy(() => import('./haliform/pages/HaliFormGate'));
const HaliForm = lazy(() => import('./haliform/HaliForm'));

const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#0a0f1a',
    gap: '16px'
  }}>
    <div style={{
      width: '36px',
      height: '36px',
      border: '3px solid rgba(59, 130, 246, 0.2)',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const LeadDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/halitrade/leads/${id}`} replace />;
};

const LeadEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/halitrade/leads/${id}/edit`} replace />;
};

const ProtectedRoute = ({ children, requiredRole }) => {
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  const { hasRequiredRole } = usePermissions();
  // SECURITY: when role check fails, redirect to a page derived from the
  // USER's actual category — NOT from getTenantConfig(), which derives the
  // tenant from window.location.pathname and would send /platform/* back
  // to /platform/dashboard, causing an infinite redirect that effectively
  // grants access.
  const safeRedirectForUser = (u) => {
    try {
      return getUserDashboardRoute(u?.category || u?.userType, u?.role);
    } catch (err) {
      console.error('[ProtectedRoute] Unknown user category — refusing customer fallback:', err.message);
      return '/login?error=unknown_category';
    }
  };

  // SEC-001 FIX: Removed email parameter authentication bypass.
  // All routes now require proper JWT/session authentication.

  // Hard short-circuit: if no token exists anywhere in client storage AND we
  // are not in the just-logged-in window, immediately redirect to login —
  // don't wait for the async checkAuthStatus to finish flipping `loading`
  // off, because for cleared-session navigation (session-timeout tests,
  // post-logout protected-route hits) we'd otherwise render briefly before
  // the Navigate fires, and tests that snapshot the URL right after
  // `page.goto()` would catch the protected path instead of /login.
  let hasAnyToken = false;
  try {
    // Only check `token` here — `refresh_token` alone is not enough to render
    // a protected route, and counting it would let session-timeout tests
    // (which clear only `token`) sneak past the sync redirect.
    hasAnyToken = !!(typeof localStorage !== 'undefined' && localStorage.getItem('token'));
  } catch (_) { /* localStorage unavailable */ }
  const justLoggedInFlag = (() => {
    try { return !!sessionStorage.getItem('just_logged_in'); } catch (_) { return false; }
  })();
  if (!hasAnyToken && !justLoggedInFlag) {
    let loginPath = '/login';
    if (location.pathname.startsWith('/haliagent')) {
      loginPath = '/haliagent/login';
    } else if (location.pathname.startsWith('/halitrade') || location.pathname.startsWith('/platform')) {
      loginPath = '/halitrade/login';
    } else if (location.pathname.startsWith('/haliflow')) {
      loginPath = '/haliflow/login';
    }
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  // Show loading while auth check is in progress
  if (loading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    // Check if user just logged in - don't redirect during async state propagation
    // Keep the flag until isAuthenticated becomes true
    const justLoggedIn = sessionStorage.getItem('just_logged_in');
    if (justLoggedIn) {
      console.log('[ProtectedRoute] Just logged in - skipping redirect, waiting for auth state');
      // Return loading state while auth propagates - DON'T remove the flag yet
      return <LoadingFallback />;
    }

    // Redirect to the appropriate login page based on current path
    let loginPath = '/login';
    if (location.pathname.startsWith('/haliagent')) {
      loginPath = '/haliagent/login';
    } else if (location.pathname.startsWith('/halitrade') || location.pathname.startsWith('/platform')) {
      loginPath = '/halitrade/login';
    } else if (location.pathname.startsWith('/haliflow')) {
      loginPath = '/haliflow/login';
    }
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  // Clear the just_logged_in flag once authenticated
  if (sessionStorage.getItem('just_logged_in')) {
    sessionStorage.removeItem('just_logged_in');
    console.log('[ProtectedRoute] Auth confirmed - cleared just_logged_in flag');
  }

  // Check required role using both RoleContext and direct user check
  // This handles the race condition where RoleContext hasn't synced yet
  if (requiredRole) {
    let hasRole = hasRequiredRole(requiredRole);

    // Fallback: check directly with AuthContext user if RoleContext check fails
    if (!hasRole && user && requiredRole === 'platform-admin') {
      hasRole = isPlatformAdmin(user);
      if (hasRole) {
        console.log('[ProtectedRoute] Role check passed via direct user check for platform-admin');
      }
    }

    if (!hasRole) {
      const safe = safeRedirectForUser(user);
      console.log('[ProtectedRoute] Role check failed for:', requiredRole, 'user:', user?.email, '→', safe);
      return <Navigate to={safe} replace />;
    }
  }

  return children;
};

const DashboardRedirect = () => {
  const tenantConfig = getTenantConfig();
  return <Navigate to={tenantConfig.defaultRoute} replace />;
};

// Domain-based landing page redirect
// Uses env-configured domains to determine the landing variant.
const DomainBasedLanding = () => {
  const hostname = getHostname();
  const halitradeDomains = toDomainList(process.env.REACT_APP_HALITRADE_DOMAINS || '');
  const landingVariant = (process.env.REACT_APP_LANDING_VARIANT || '').toLowerCase();

  const shouldUseHaliTradeLanding =
    landingVariant === 'halitrade' ||
    (halitradeDomains.length > 0 && hostMatchesList(hostname, halitradeDomains));

  if (shouldUseHaliTradeLanding) {
    return <Navigate to="/halitrade-landing" replace />;
  }

  // Default to HaliSoft landing for other domains
  return <Navigate to="/landing" replace />;
};

const PlatformRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/platform', '/haliflow');
  return <Navigate to={newPath} replace />;
};

function PlatformRoutes() {
  return (
    <PlatformLayout>
      <Routes>
        <Route path="dashboard" element={<PlatformDashboard />} />
        <Route path="dashboard-new" element={<PlatformDashboardNew />} />
        <Route path="product-pricing" element={<ProductPricingAdmin />} />
        <Route path="risk-matrix" element={<PlatformRiskMatrixPage />} />
        <Route path="credit-insurance" element={<CreditInsurancePage />} />
        <Route path="repayment-pricing" element={<RepaymentPricingSettings />} />
        <Route path="factor-tools" element={<PlatformFactorTools />} />
        <Route path="doc-agent" element={<HaliDocAgentPage />} />
        <Route path="doc-agent/:docRef" element={<HaliDocAgentPage />} />
        <Route path="factor-tools/onboarding-studio" element={<OnboardingStudioHaliTools />} />
        <Route path="factor-tools/financial-analysis" element={<FinancialAgent />} />
        <Route path="factor-tools/financial-analysis/:recordId" element={<FinancialAgent />} />
        <Route path="factor-tools/invoice-financing" element={<InvoiceFinancing />} />
        <Route path="factor-tools/invoice-financing-check" element={<InvoiceFinancingCheck />} />
        <Route path="factor-tools/trade-document-risk" element={<TradeDocumentRiskAnalyzerGate />} />
        <Route path="factor-tools/trade-document-risk/:recordId" element={<TradeDocumentRiskAnalyzerGate />} />
        <Route path="factor-tools/past-trade-studio" element={<PastTradeStudio />} />
        <Route path="factor-tools/past-trade-studio/:recordId" element={<PastTradeStudio />} />
        <Route path="factor-tools/contract-analyzer" element={<ContractAgent />} />
        <Route path="factor-tools/contract-analyzer/:recordId" element={<ContractAgent />} />
        <Route path="factor-tools/legal-agent" element={<ContractAgent />} />
        <Route path="factor-tools/legal-agent/:recordId" element={<ContractAgent />} />
        <Route path="factor-tools/financial-agent" element={<FinancialAgentStudio />} />
        <Route path="factor-tools/financial-agent/:recordId" element={<FinancialAgentStudio />} />
        <Route path="factor-tools/invoice-financing" element={<InvoiceFinancing />} />
        <Route path="factor-tools/financing-dashboard" element={<CustomerFinancingDashboard />} />
        <Route path="factor-tools/financial-document-analyzer" element={<FinancialDocumentAnalyzer />} />
        <Route path="factor-tools/bl-analyzer" element={<BLAnalyzer />} />
        <Route path="factor-tools/kyc-ai" element={<KycAIAgent />} />
        <Route path="factor-tools/agent-kyc" element={<AgentKYC />} />
        <Route path="factor-tools/kyc-agent" element={<KYCAgentTest />} />
        <Route path="factor-tools/bank-statement" element={<BankStatementPage />} />
        <Route path="factor-tools/risk-analysis" element={<RiskAnalysis />} />
        <Route path="risk-analysis" element={<RiskAnalysis />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/new" element={<LeadForm />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="leads/:id/edit" element={<LeadForm />} />
        <Route path="factor-tools/past-trade-agent" element={<PastTradeAgent />} />
        <Route path="factor-tools/past-trade-agent/:recordId" element={<PastTradeAgent />} />
        <Route path="validation-queue" element={<ValidationQueue />} />
        <Route path="onboarding-submissions" element={<OnboardingSubmissions />} />
        <Route path="halipass" element={<HaliPassDashboard />} />
        <Route path="financing-hub" element={<PlatformFinancingDashboard />} />
        <Route path="financing-hub/marketplace" element={
          <PermissionGuard permissionKey={SIDEBAR_ITEM_KEYS.MARKETPLACE}>
            <PlatformMarketplaceDashboard />
          </PermissionGuard>
        } />
        <Route path="financing-hub/wallets" element={
          <PermissionGuard permissionKey={SIDEBAR_ITEM_KEYS.FINANCING_HUB_ADMIN}>
            <WalletDashboard />
          </PermissionGuard>
        } />
        <Route path="arpa" element={<WhiteLabelARPAWithOIF />} />
        <Route path="auto-claude" element={<AutoClaudeDashboard />} />
        <Route path="auto-claude/:taskId" element={<AutoClaudeTaskDetail />} />
        <Route path="haliblog" element={<HaliBlog isAdmin={true} />} />
        <Route path="haliblog/settings" element={<HaliBlogSettings />} />
        <Route path="wallet" element={<EWalletNew userType="platform" />} />
        <Route path="wallet-old" element={<EWallet userType="platform" />} />
        <Route path="financing-hub/risk-analysis" element={<RiskValidationDashboard />} />
        <Route path="financing-hub/withdraw" element={<WithdrawalRequest />} />
        <Route path="financing-hub/finance-team" element={<FinanceTeamDashboard />} />
        <Route path="sent-termsheets" element={<SentTermsheets />} />
        <Route path="halidoc" element={<HaliDocPage mode="platform" />} />
        <Route path="lctradedoccheck" element={<LCTradeDocCheck />} />
        <Route path="haliform" element={<HaliForm />} />
        <Route path="halitrade/relationship-maps" element={<HaliRiskListPage />} />
        <Route path="halitrade/halirisk-playbook" element={<HaliRiskV2Page />} />
        <Route path="noa/:noaId" element={<NOADetailPage />} />
        <Route path="referrals" element={<ReferralManagement />} />
        <Route path="customers" element={<CustomersManagement />} />
        <Route path="customer-chat" element={<CustomerChatSupport />} />
        <Route path="my-company" element={<CompanyModule />} />
        <Route path="companies" element={<MyCompanies />} />
        <Route path="companies/:companyId" element={<CompanyModule />} />
        <Route path="companies/:companyId/contacts/:contactId" element={<ContactDetailPage />} />
        <Route path="trade-partners/:tradePartnerId" element={<TradePartnerModule />} />
        <Route path="trade-partners/:tradePartnerId/relationship-map" element={<RelationshipMap />} />
        <Route path="trade-partners/:tradePartnerId/relationship-map/:mapRef" element={<RelationshipMap />} />
        <Route path="trade-partners/:tradePartnerId/contacts/:contactId" element={<ContactDetailPage />} />
        <Route path="trade-partners" element={<TradePartnerModule />} />
        <Route path="data-explorer" element={<DataExplorer />} />
        <Route path="user-management" element={<UserManagementModule />} />
        <Route path="settings/permissions" element={<SettingsPermissions />} />
        <Route path="settings/permissions/user/:userId" element={<UserDetailPage />} />
        <Route path="settings/object-permissions" element={<ObjectPermissions />} />
        <Route path="analytics" element={<AnalyticsHub />} />
        <Route path="analytics/termsheets" element={<SalesTermsheet />} />
        <Route path="analytics/termsheets/sales-owner" element={<SalesOwnerDetailPage />} />
        <Route path="analytics/termsheets/detail" element={<TermsheetDetailPage />} />
        <Route path="analytics/termsheets/monthly-kpi" element={<MonthlyKPIDetailPage />} />
        <Route path="analytics/credit-limit" element={<CreditLimitDashboard />} />
        <Route path="analytics/financed" element={<FinancedDashboard />} />
        <Route path="analytics/financed/detail" element={<FinancedDetailPage />} />
        <Route path="analytics/aum" element={<AumSalesProductDashboard />} />
        <Route path="analytics/opportunities" element={<OpportunitiesDashboard />} />
        <Route path="analytics/signup" element={<SignUpDashboard />} />
        <Route path="analytics/signup" element={<SignUpDashboard />} />
        <Route path="approvals" element={<ApprovalQueue />} />
        <Route path="wallet-approvals" element={<WalletApprovals />} />
        <Route path="transaction-monitor" element={<TransactionMonitor />} />
        <Route path="wallet/:walletId/deposit" element={<WalletDepositPage />} />
        <Route path="wallet/:walletId/withdraw" element={<WalletWithdrawPage />} />
        <Route path="wallet/:walletId/transaction/:transactionId" element={<TransactionDetailPage />} />
        <Route path="wallet/:walletId/deposit/:depositId" element={<DepositDetailPage />} />
        <Route path="wallet/:walletId/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
        <Route path="wallet/deposit" element={<WalletDepositPage />} />
        <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
        <Route path="wallet/transaction/:transactionId" element={<TransactionDetailPage />} />
        <Route path="wallet/deposit/:depositId" element={<DepositDetailPage />} />
        <Route path="wallet/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
        <Route path="wallet/:walletId" element={<EWalletNew userType="platform" />} />
        <Route path="invoice-financing/:id" element={
          <PermissionGuard permissionKey={SIDEBAR_ITEM_KEYS.INVOICE_DETAIL}>
            <InvoiceFinancingDetail />
          </PermissionGuard>
        } />
        <Route path="invoice-financing/:id/allocation/:allocationId" element={
          <PermissionGuard permissionKey={SIDEBAR_ITEM_KEYS.INVOICE_DETAIL}>
            <AllocationDetailPage />
          </PermissionGuard>
        } />
        <Route path="opportunity/:id" element={<InvestorInvoiceDetail />} />
        <Route path="onboarding-opportunity/:opportunityId" element={<OpportunityDetailPage />} />
        <Route path="termsheet/ts/:termsheetId" element={<HaliFlowTermsheetPage />} />
        <Route path="termsheet/:leadId" element={<HaliFlowTermsheetPage />} />
        {/* "Coming soon" placeholders for modules referenced in nav but not yet built.
            Without these, the catch-all redirects to dashboard with no feedback. */}
        <Route path="marketplace" element={<ComingSoon moduleName="Marketplace" description="The standalone marketplace view is being unified into the Financing Hub. The active marketplace lives at Financing Hub → Marketplace." />} />
        {/* HaliRisk product lives at halitrade/halirisk-playbook — do not leave a ComingSoon dead-end */}
        <Route path="halirisk" element={<Navigate to="/platform/halitrade/halirisk-playbook" replace />} />
        <Route path="halilab" element={<ComingSoon moduleName="HaliLab" />} />
        <Route path="users" element={<ComingSoon moduleName="User Management" />} />
        <Route path="settings" element={<ComingSoon moduleName="Platform Settings" />} />
        <Route path="blog" element={<ComingSoon moduleName="Blog Admin" description="Use the Haliblog admin route for now." />} />
        <Route path="financing-hub/audit" element={<ComingSoon moduleName="Audit Trail" />} />
        <Route path="financing-hub/transactions" element={<ComingSoon moduleName="Transactions" />} />
        <Route path="financing-hub/funders" element={<ComingSoon moduleName="Funders" />} />
        <Route path="financing-hub/investors" element={<ComingSoon moduleName="Investors" />} />
        <Route path="financing-hub/approvals" element={<ComingSoon moduleName="Approvals" description="Use the platform Approvals route for the maker-checker queue." />} />
        <Route path="financing-hub/disbursements" element={<ComingSoon moduleName="Disbursements" />} />
        <Route path="financing-hub/repayments" element={<ComingSoon moduleName="Repayments" />} />
        <Route path="financing-hub/reports" element={<ComingSoon moduleName="Reports" />} />
        <Route path="/" element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/platform/dashboard" replace />} />
      </Routes>
    </PlatformLayout>
  );
}

const HaliFlowRouter = () => {
  const { user } = useAuth();
  const userType = user?.category?.toLowerCase();
  const userRole = user?.role?.toLowerCase() || '';
  const location = useLocation();
  const useLegacyInvestorRoutes = location.pathname.startsWith('/investor');

  console.log('[HaliFlowRouter] Routing decision - category:', userType, 'role:', userRole);

  if (userType === 'platform_user' || userType === 'platform' || userType === 'platform_admin'
    || userRole.includes('platform') || userRole.includes('admin')) {
    return <PlatformRoutes />;
  }
  // Funders/investors: match on role too. Many funder accounts have a blank or
  // mistaken 'customer' category, so keying off category alone dropped them into
  // the customer portal. role is the reliable signal here.
  if (userType === 'investor' || userType === 'funder'
    || userRole.includes('investor') || userRole.includes('funder')) {
    return useLegacyInvestorRoutes ? <InvestorLegacyRoutes /> : <HalifundRoutes />;
  }
  if (userType === 'buyer') {
    return <BuyerRoutes />;
  }
  if (userType === 'customer' || userRole.includes('customer')) {
    return <CustomerRoutes />;
  }
  // Referral partners get the Referral portal (new dashboard, onboarding, wallet)
  if (userType === 'referral' || userRole === 'referral') {
    console.log('[HaliFlowRouter] → Routing to ReferralRoutes (Referral partner)');
    // If they landed on /haliflow/*, send them to /referral so URL and content match
    if (location.pathname.startsWith('/haliflow')) {
      return <Navigate to="/referral/dashboard" replace />;
    }
    return <ReferralRoutes />;
  }
  // Route agents/brokers to HaliTradeLayout (shows HaliAgent branding)
  if (userType === 'agent' || userType === 'broker' || userType === 'halitrade' ||
    userRole.includes('agent') || userRole.includes('broker')) {
    console.log('[HaliFlowRouter] → Routing to HaliTradeRoutes (Agent/HaliTrade user)');
    return <HaliTradeRoutes />;
  }
  console.log('[HaliFlowRouter] → Routing to CustomerRoutes (default)');
  return <CustomerRoutes />;
};

// CustomerEntry - redirects platform users to platform portal (preserve deep links)
const CustomerEntry = () => {
  const { user } = useAuth();
  const location = useLocation();
  const category = String(user?.category || user?.userType || '').toLowerCase();
  const role = String(user?.role || '').toLowerCase();
  // Platform identity is category-based (role=admin alone is NOT enough — company admins exist)
  const isPlatformUser = category.includes('platform');

  if (isPlatformUser) {
    // Keep Factor Tools / KYC deep links instead of dumping to dashboard
    if (location.pathname.startsWith('/customer/factor-tools')) {
      return (
        <Navigate
          to={location.pathname.replace(/^\/customer/, '/platform') + location.search}
          replace
        />
      );
    }
    return <Navigate to="/platform/dashboard" replace />;
  }

  // Funders/investors must never see the customer portal even when their stored
  // category is blank or mistakenly 'customer' — route by role too. Without this,
  // a funder hitting /customer/* (e.g. /customer/marketplace) saw customer access.
  if (category === 'investor' || category === 'funder'
    || role.includes('investor') || role.includes('funder')) {
    return <Navigate to="/halifund/dashboard" replace />;
  }

  return <CustomerRoutes />;
};

const CustomerRoutes = () => (
  <CustomerLayout>
    <Routes>
      <Route path="dashboard" element={<CustomerDashboard />} />
      <Route path="wallet" element={<EWalletNew userType="customer" />} />
      <Route path="wallet-old" element={<EWallet userType="customer" />} />
      <Route path="wallet/:walletId" element={<EWallet userType="customer" />} />
      <Route path="wallet/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="onboarding-choice" element={<OnboardingChoice />} />
      <Route path="invoice-onboarding" element={<OnboardingChoice />} />
      <Route path="invoice-onboarding/automatic" element={<OnboardingStudioHaliTools />} />
      <Route path="invoice-onboarding/manual" element={<OnboardingStudioHaliTools />} />
      <Route path="invoice-financing" element={<CustomerInvoiceFinancing />} />
      <Route path="submit-invoice" element={<InvoiceSubmissionPage />} />
      <Route path="trade-partner-invoices" element={<TradePartnerInvoicesPage />} />
      <Route path="invoice-financing/:id" element={
        <PermissionGuard permissionKey={SIDEBAR_ITEM_KEYS.INVOICE_DETAIL}>
          <InvoiceFinancingDetail />
        </PermissionGuard>
      } />
      <Route path="marketplace" element={
        <PermissionGuard permissionKey={SIDEBAR_ITEM_KEYS.MARKETPLACE}>
          <PlatformMarketplaceDashboard tenantType="customer" />
        </PermissionGuard>
      } />
      <Route path="my-submissions" element={<MyOnboardingSubmissions />} />
      <Route path="my-termsheets" element={<MyTermsheets />} />
      <Route path="my-contracts" element={<MyContracts />} />
      <Route path="halidoc" element={<HaliDocPage mode="customer" />} />
      <Route path="doc-agent" element={<HaliDocAgentPage />} />
      <Route path="doc-agent/:docRef" element={<HaliDocAgentPage />} />
      <Route path="noa/:noaId" element={<NOADetailPage />} />
      <Route path="my-companies" element={<MyCompanies />} />
      <Route path="companies" element={<MyCompanies />} />
      <Route path="companies/:companyId" element={<CompanyModule />} />
      <Route path="trade-partners/:tradePartnerId" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map" element={<RelationshipMap />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map/:mapRef" element={<RelationshipMap />} />
      <Route path="agent/invoice-financing" element={<InvoiceFinancingGuideAgent />} />
      <Route path="messages" element={<CustomerMessages />} />
      <Route path="agent/onboarding" element={<CustomerAgent />} />
      <Route path="company" element={<CompanyModule />} />
      <Route path="my-company" element={<CompanyModule />} />
      <Route path="trade-partners" element={<TradePartnerModule />} />
      <Route path="validation-status" element={<ValidationStatus />} />
      <Route path="termsheet/ts/:termsheetId" element={<HaliFlowTermsheetPage />} />
      <Route path="termsheet/:leadId" element={<HaliFlowTermsheetPage />} />
      <Route path="onboarding-opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="lctradedoccheck" element={<LCTradeDocCheck />} />
      <Route path="haliform" element={<HaliForm />} />
      <Route path="settings" element={<AgentSettingsPage />} />
      {/* Factor Tools for customers */}
      <Route path="factor-tools" element={<PlatformFactorTools />} />
      <Route path="factor-tools/invoice-financing-check" element={<InvoiceFinancingCheck />} />
      <Route path="factor-tools/financial-analysis" element={<FinancialAgent />} />
      <Route path="factor-tools/financial-analysis/:recordId" element={<FinancialAgent />} />
      <Route path="factor-tools/contract-analyzer" element={<ContractAgent />} />
      <Route path="factor-tools/contract-analyzer/:recordId" element={<ContractAgent />} />
      <Route path="factor-tools/trade-document-risk" element={<TradeDocumentRiskAnalyzerGate />} />
      <Route path="factor-tools/trade-document-risk/:recordId" element={<TradeDocumentRiskAnalyzerGate />} />
      <Route path="factor-tools/past-trade-studio" element={<PastTradeStudio />} />
      <Route path="factor-tools/past-trade-studio/:recordId" element={<PastTradeStudio />} />
      <Route path="factor-tools/kyc-ai" element={<KycAIAgent />} />
      <Route path="factor-tools/bank-statement" element={<BankStatementPage />} />
      <Route path="factor-tools/risk-analysis" element={<RiskAnalysis />} />
      <Route path="factor-tools/invoice-financing" element={<InvoiceFinancing />} />
      <Route path="factor-tools/financial-document-analyzer" element={<FinancialDocumentAnalyzer />} />
      <Route path="halipass" element={<HaliPassDashboard />} />
      {/* Fallback route for customer - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/customer/dashboard" replace />} />
    </Routes>
  </CustomerLayout>
);

const ReferralRoutes = () => (
  <ReferralLayout>
    <Routes>
      <Route path="dashboard" element={<ReferralDashboardNew />} />
      <Route path="refer" element={<ReferralSubmissionForm />} />
      <Route path="invoice-onboarding" element={<ReferralSubmissionForm />} />
      <Route path="invoice-onboarding/automatic" element={<OnboardingStudioHaliTools />} />
      <Route path="invoice-onboarding/manual" element={<OnboardingStudioHaliTools />} />
      <Route path="onboarding-choice" element={<ReferralSubmissionForm />} />
      <Route path="opportunities" element={<ReferralDashboard />} />
      <Route path="opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="onboarding-opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="termsheet/ts/:termsheetId" element={<TermsheetPage />} />
      <Route path="termsheet/:leadId" element={<TermsheetPage />} />
      <Route path="my-termsheets" element={<MyTermsheets />} />
      <Route path="referral-dashboard" element={<ReferralDashboard />} />
      <Route path="settings" element={<AgentSettingsPage />} />
      <Route path="company" element={<CompanyModule />} />
      <Route path="haliform" element={<HaliForm />} />
      <Route path="companies" element={<MyCompanies />} />
      <Route path="companies/:companyId" element={<CompanyModule />} />
      <Route path="trade-partners" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map" element={<RelationshipMap />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map/:mapRef" element={<RelationshipMap />} />
      <Route path="trade-partners/:tradePartnerId/contacts/:contactId" element={<ContactDetailPage />} />
      <Route path="wallet" element={<EWalletNew userType="referral" />} />
      <Route path="wallet-old" element={<EWallet userType="referral" />} />
      <Route path="wallet/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
      <Route path="/" element={<Navigate to="/referral/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/referral/dashboard" replace />} />
    </Routes>
  </ReferralLayout>
);



const HaliTradeRoutes = () => (
  <HaliTradeLayout>
    <Routes>
      <Route path="dashboard" element={<AgentDashboardNew />} />
      <Route path="dashboard-old" element={<HaliTradeDashboard />} />
      {/* Customer/Agent routes from CustomerRoutes */}
      <Route path="wallet" element={<EWalletNew userType="customer" />} />
      <Route path="wallet-old" element={<EWallet userType="customer" />} />
      <Route path="wallet/:walletId" element={<EWallet userType="customer" />} />
      <Route path="wallet/:walletId/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/:walletId/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/:walletId/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/:walletId/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/:walletId/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="wallet/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="onboarding-choice" element={<OnboardingChoice />} />
      <Route path="invoice-onboarding" element={<OnboardingChoice />} />
      <Route path="invoice-onboarding/automatic" element={<OnboardingStudioHaliTools />} />
      <Route path="invoice-onboarding/manual" element={<OnboardingStudioHaliTools />} />
      <Route path="submit-invoice" element={<InvoiceSubmissionPage />} />
      <Route path="trade-partner-invoices" element={<TradePartnerInvoicesPage />} />
      <Route path="my-submissions" element={<MyOnboardingSubmissions />} />
      <Route path="my-contracts" element={<MyContracts />} />
      <Route path="my-termsheets" element={<MyTermsheets />} />
      <Route path="my-companies" element={<MyCompanies />} />
      <Route path="halidoc" element={<HaliDocPage mode="customer" />} />
      <Route path="haliform" element={<HaliForm />} />
      <Route path="halipass" element={<HaliPassDashboard />} />
      <Route path="noa/:noaId" element={<NOADetailPage />} />
      <Route path="marketplace" element={<PlatformMarketplaceDashboard tenantType="customer" />} />
      <Route path="factor-tools" element={<PlatformFactorTools />} />
      <Route path="doc-agent" element={<HaliDocAgentPage />} />
      <Route path="doc-agent/:docRef" element={<HaliDocAgentPage />} />
      <Route path="factor-tools/financial-analysis" element={<FinancialAgent />} />
      <Route path="factor-tools/financial-analysis/:recordId" element={<FinancialAgent />} />
      <Route path="factor-tools/invoice-financing" element={<InvoiceFinancing />} />
      <Route path="factor-tools/invoice-financing-check" element={<InvoiceFinancingCheck />} />
      <Route path="factor-tools/trade-document-risk" element={<TradeDocumentRiskAnalyzerGate />} />
      <Route path="factor-tools/trade-document-risk/:recordId" element={<TradeDocumentRiskAnalyzerGate />} />
      <Route path="factor-tools/past-trade-studio" element={<PastTradeStudio />} />
      <Route path="factor-tools/past-trade-studio/:recordId" element={<PastTradeStudio />} />
      <Route path="factor-tools/contract-analyzer" element={<ContractAgent />} />
      <Route path="factor-tools/contract-analyzer/:recordId" element={<ContractAgent />} />
      <Route path="factor-tools/risk-analysis" element={<RiskAnalysis />} />
      <Route path="risk-analysis" element={<RiskAnalysis />} />
      <Route path="factor-tools/financial-document-analyzer" element={<FinancialDocumentAnalyzer />} />
      <Route path="factor-tools/kyc-ai" element={<KycAIAgent />} />
      <Route path="factor-tools/bank-statement" element={<BankStatementPage />} />
      <Route path="factor-tools/past-trade-agent" element={<PastTradeAgent />} />
      <Route path="factor-tools/past-trade-agent/:recordId" element={<PastTradeAgent />} />
      <Route path="factor-tools/legal-agent" element={<ContractAgent />} />
      <Route path="factor-tools/legal-agent/:recordId" element={<ContractAgent />} />
      <Route path="factor-tools/financial-agent" element={<FinancialAgentStudio />} />
      <Route path="factor-tools/financial-agent/:recordId" element={<FinancialAgentStudio />} />
      {/* <Route path="factor-tools/financial-report-analysis-2025" element={<FinancialReportAnalysis24122025 />} /> */}
      <Route path="supply-chain" element={<CustomerDashboard />} />
      <Route path="invoice-financing" element={<CustomerInvoiceFinancing />} />
      <Route path="agent/invoice-financing" element={<InvoiceFinancingGuideAgent />} />
      <Route path="company" element={<CompanyModule />} />
      <Route path="my-company" element={<CompanyModule />} />
      <Route path="companies" element={<MyCompanies />} />
      <Route path="companies/:companyId" element={<CompanyModule />} />
      <Route path="settings" element={<AgentSettingsPage />} />
      <Route path="trade-partners" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map" element={<RelationshipMap />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map/:mapRef" element={<RelationshipMap />} />
      <Route path="messages" element={<CustomerMessages />} />
      <Route path="validation-status" element={<ValidationStatus />} />
      <Route path="user-management" element={<UserManagementModule />} />
      <Route path="settings/permissions" element={<SettingsPermissions />} />
      <Route path="settings/permissions/user/:userId" element={<UserDetailPage />} />
      <Route path="debug" element={<HaliTradeDebugConsole />} />
      <Route path="termsheet/ts/:termsheetId" element={<TermsheetPage />} />
      <Route path="termsheet/:leadId" element={<TermsheetPage />} />
      <Route path="opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="onboarding-opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="list" element={<PlatformListPage />} />
      <Route path="relationship-maps" element={<HaliRiskListPage />} />
      <Route path="halirisk-playbook" element={<HaliRiskPlaybookDashboard />} />
      <Route path="lctradedoccheck" element={<LCTradeDocCheck />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="leads/new" element={<LeadForm />} />
      <Route path="leads/:id" element={<LeadDetail />} />
      <Route path="leads/:id/edit" element={<LeadForm />} />
      <Route path="/" element={<Navigate to="/halitrade/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/halitrade/dashboard" replace />} />
    </Routes>
  </HaliTradeLayout>
);

const HaliTradeEntry = () => {
  const { user } = useAuth();
  const location = useLocation();
  const category = String(user?.category || user?.userType || '').toLowerCase();
  const role = String(user?.role || '').toLowerCase();
  const isPlatformUser = category.includes('platform');
  if (isPlatformUser) {
    const path = location.pathname.replace(/^\/halitrade/, '');
    if (path === '/haliform' || path === 'haliform') {
      return <Navigate to="/platform/haliform" replace />;
    }
    // Preserve factor-tools deep links (bare /halitrade/factor-tools/* → /platform/...)
    if (location.pathname.startsWith('/halitrade/factor-tools')) {
      return (
        <Navigate
          to={location.pathname.replace(/^\/halitrade/, '/platform') + location.search}
          replace
        />
      );
    }
    return <Navigate to="/platform/dashboard" replace />;
  }
  if (category.includes('customer')) {
    const target = location.pathname.replace(/^\/halitrade/, '/customer') + location.search;
    return <Navigate to={target || '/customer/dashboard'} replace />;
  }
  // Referral partners → Referral portal (new dashboard, not HaliTrade agent view)
  if (category === 'referral' || role === 'referral') {
    return <Navigate to="/referral/dashboard" replace />;
  }
  // Investors / funders never use the HaliTrade agent workspace — including the
  // factor-tools suite (invoice-financing, etc.). Without this they fell through
  // to <HaliTradeRoutes /> and could reach /halitrade/factor-tools/* with an
  // "INVESTOR" role badge. Send them to their own portal instead.
  if (category.includes('investor') || category.includes('funder') || role.includes('investor')) {
    return <Navigate to="/investor/dashboard" replace />;
  }
  return <HaliTradeRoutes />;
};

// ReferralEntry - redirect non-referral users to their correct dashboard
const ReferralEntry = () => {
  const { user } = useAuth();
  const category = String(user?.category || user?.userType || '').toLowerCase();
  const role = String(user?.role || '').toLowerCase();
  const isReferral = category === 'referral' || role === 'referral';

  if (!isReferral) {
    try {
      return <Navigate to={getUserDashboardRoute(category || user?.category, role || user?.role)} replace />;
    } catch (err) {
      console.error('[ReferralEntry] Unknown category — refusing customer fallback:', err.message);
      return <Navigate to="/login?error=unknown_category" replace />;
    }
  }
  return <ReferralRoutes />;
};

// HaliAgent Routes - for agents/brokers (uses same layout as HaliTrade)
// Dashboard: new modular Referral dashboard; dashboard-old = legacy HaliTradeDashboard
const HaliAgentRoutes = () => (
  <HaliTradeLayout>
    <Routes>
      <Route path="dashboard" element={<ReferralDashboardNew />} />
      <Route path="dashboard-old" element={<HaliTradeDashboard />} />
      {/* Agent workspace routes (must exist or sidebar will bounce to dashboard) */}
      <Route path="invoice-onboarding" element={<OnboardingChoice />} />
      <Route path="invoice-onboarding/automatic" element={<OnboardingStudioHaliTools />} />
      <Route path="invoice-onboarding/manual" element={<OnboardingStudioHaliTools />} />
      <Route path="onboarding-choice" element={<OnboardingChoice />} />
      <Route path="my-submissions" element={<MyOnboardingSubmissions />} />
      <Route path="halidoc" element={<HaliDocPage mode="customer" />} />
      <Route path="wallet" element={<EWalletNew userType="customer" />} />
      <Route path="wallet-old" element={<EWallet userType="customer" />} />
      <Route path="wallet/:walletId" element={<EWallet userType="customer" />} />
      {/* Wallet flows without walletId (EWallet uses these) */}
      <Route path="wallet/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="wallet/:walletId/deposit" element={<WalletDepositPage />} />
      <Route path="wallet/:walletId/withdraw" element={<WalletWithdrawPage />} />
      <Route path="wallet/:walletId/transaction/:transactionId" element={<TransactionDetailPage />} />
      <Route path="wallet/:walletId/deposit/:depositId" element={<DepositDetailPage />} />
      <Route path="wallet/:walletId/withdrawal/:withdrawalId" element={<WithdrawalDetailPage />} />
      <Route path="settings" element={<AgentSettingsPage />} />
      <Route path="trade-partners" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId" element={<TradePartnerModule />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map" element={<RelationshipMap />} />
      <Route path="trade-partners/:tradePartnerId/relationship-map/:mapRef" element={<RelationshipMap />} />
      <Route path="messages" element={<CustomerMessages />} />
      <Route path="opportunities" element={<PlatformListPage />} />
      <Route path="opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="onboarding-opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="termsheet/ts/:termsheetId" element={<TermsheetPage />} />
      <Route path="termsheet/:leadId" element={<TermsheetPage />} />
      <Route path="/" element={<Navigate to="/haliagent/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/haliagent/dashboard" replace />} />
    </Routes>
  </HaliTradeLayout>
);

const HaliLabRoutes = () => (
  <HaliLabLayout>
    <Routes>
      <Route path="dashboard" element={<HaliLabDashboard />} />
      <Route path="prototypes" element={<HaliLabDashboard />} />
      <Route path="prototypes/invoice-submission" element={<InvoiceSubmissionForm />} />
      <Route path="halifin" element={<HaliFinAgent />} />
      <Route path="risk-analysis" element={<HaliRiskAgent />} />
      <Route path="risk-analysis" element={<HaliRiskAgent />} />
      <Route path="contract-analyzer" element={<HaliContractAgent />} />
      <Route path="contract-analyzer/:recordId" element={<HaliContractAgent />} />
      <Route path="poc-tools" element={<HaliLabDashboard />} />
      <Route path="experiments" element={<HaliLabDashboard />} />
      <Route path="feedback" element={<HaliLabDashboard />} />
      <Route path="documentation" element={<HaliLabDashboard />} />
      <Route path="leads" element={<LeadDashboard />} />
      <Route path="leads/new" element={<LeadForm />} />
      <Route path="leads/:id" element={<LeadDetail />} />
      <Route path="leads/:id/edit" element={<LeadForm />} />
      <Route path="/" element={<Navigate to="/halilab/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/halilab/dashboard" replace />} />
    </Routes>
  </HaliLabLayout>
);

// GA4 route change tracker — sends page_view on every SPA navigation
const GA4RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);
  return null;
};

const AppRouter = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GA4RouteTracker />
      <RoleProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public Routes - Debugging Priority */}
            <Route path="/eva/chat" element={<EvaChatPage />} />
            <Route path="/hali-leads-agent" element={<HaliLeadsAgentPage />} />
            {/* Passwordless marketing entry — must NOT steal authenticated /invoice-onboarding */}
            <Route path="/haliagent/try" element={<AutoHaliAgentEntry />} />
            <Route path="/try-haliagent" element={<AutoHaliAgentEntry />} />
            {/* Login routes */}
            <Route path="/login" element={<ModernAuthModuleV2 />} />
            <Route path="/halitrade/login" element={<ModernAuthModuleV2 />} />
            <Route path="/haliflow/login" element={<ModernAuthModuleV2 />} />
            <Route path="/haliagent/login" element={<ModernAuthModuleV2 />} />
            <Route path="/halilab/login" element={<ModernAuthModuleV2 />} />
            {/* Signup routes - redirect to login with mode=signup */}
            <Route path="/signup" element={<Navigate to="/login?mode=signup" replace />} />
            <Route path="/register" element={<Navigate to="/login?mode=signup" replace />} />
            <Route path="/halitrade/signup" element={<Navigate to="/halitrade/login?mode=signup" replace />} />
            <Route path="/halitrade/register" element={<Navigate to="/halitrade/login?mode=signup" replace />} />
            <Route path="/haliflow/signup" element={<Navigate to="/haliflow/login?mode=signup" replace />} />
            <Route path="/haliagent/signup" element={<Navigate to="/haliagent/login?mode=signup" replace />} />
            <Route path="/haliagent/register" element={<Navigate to="/haliagent/login?mode=signup" replace />} />
            <Route path="/halilab/signup" element={<Navigate to="/halilab/login?mode=signup" replace />} />
            <Route path="/halilab/register" element={<Navigate to="/halilab/login?mode=signup" replace />} />
            {/* Legacy routes */}
            <Route path="/login-v2" element={<ModernAuthModuleV2 />} />
            <Route path="/halitrade/login-v2" element={<ModernAuthModuleV2 />} />
            <Route path="/factor-tools/contract-analyzer" element={<Navigate to="/halilab/contract-analyzer" replace />} />
            <Route path="/factor-tools/legal-agent" element={<Navigate to="/halilab/contract-analyzer" replace />} />
      <Route path="/halihub/risk-analysis" element={<Navigate to="/halilab/risk-analysis" replace />} />
            <Route path="/buyer/magic/:token" element={<BuyerMagicLinkPage />} />
            <Route path="/onboarding/:token" element={<OnboardingPortal />} />
            <Route path="/" element={<DomainBasedLanding />} />
            <Route path="/landing" element={<HaliSoftLanding />} />
            <Route path="/about" element={<HaliSoftAbout />} />
            {/* HaliBlog - AI-generated Trade Finance content */}
            <Route path="/blog" element={<HaliBlog />} />
            <Route path="/blog/:slug" element={<HaliBlogPost />} />
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-linkedin" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/loic" element={<LoicLanding />} />
            <Route path="/services" element={<HaliSoftServices />} />
            <Route path="/contact" element={<HaliSoftContact />} />
            <Route path="/calendly" element={<HaliSoftCalendly />} />
            <Route path="/pricing" element={<HaliSoftPricing />} />
            <Route path="/subscription" element={<SubscriptionPricing />} />
            <Route path="/subscription/haliform" element={<HaliFormMarketing />} />
            <Route path="/subscription/haliform/start" element={<HaliFormGate mode="start" />} />
            <Route path="/halisoft-pricing" element={<HaliSoftPricing />} />

            <Route path="/products" element={<HaliSoftProducts />} />
            <Route path="/products/:slug" element={<ProductBrochure />} />
            {/* New HaliSoft pages */}
            <Route path="/ai-tools" element={<HaliSoftAITools />} />
            <Route path="/ai-tools/:slug" element={<AIToolBrochure />} />
            {/* Short public URL — without this, /halirisk matches nothing and renders a blank page */}
            <Route path="/halirisk" element={<Navigate to="/ai-tools/halirisk" replace />} />
            <Route path="/haliverify" element={<HaliVerify />} />
            <Route path="/faq" element={<HaliSoftFAQ />} />
            <Route path="/halitrade-landing" element={<HaliFlowLanding />} />
            <Route path="/haliflow-landing" element={<Navigate to="/halitrade-landing" replace />} />
            <Route path="/halitrade-pricing" element={<HaliTradePricing />} />
            <Route path="/halitrade-contact" element={<HaliTradeContact />} />
            {/* New HaliTrade pages */}
            <Route path="/halitrade-ai-tools" element={<HaliTradeAITools />} />
            <Route path="/halitrade-faq" element={<HaliTradeFAQ />} />
            <Route path="/haliagent-faq" element={<HaliAgentFAQ />} />
            <Route path="/haliagent-landing" element={<HaliTradeLanding />} />
            <Route path="/halitrade-landing-old" element={<Navigate to="/haliagent-landing" replace />} />
            <Route path="/platform" element={<InvoicePlatformPage />} />
            <Route path="/user-management" element={<Navigate to="/halitrade/user-management" replace />} />
            <Route path="/companies" element={<Navigate to="/halitrade/companies" replace />} />
            <Route path="/leads" element={<Navigate to="/halitrade/leads" replace />} />
            <Route path="/leads/new" element={<Navigate to="/halitrade/leads/new" replace />} />
            <Route path="/leads/:id" element={<LeadDetailRedirect />} />
            <Route path="/leads/:id/edit" element={<LeadEditRedirect />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/financial-audit" element={<FinancialAuditAnalyzer />} />
            {/* Legacy admin settings URL (customer-portal style): product pricing */}
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <PlatformLayout>
                    <ProductPricingAdmin />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />
            {/* Smart dashboard redirect */}
            <Route path="/dashboard" element={<DashboardRedirect />} />
            {/* Dev routes */}
            <Route path="/dev/invoice-ocr" element={<InvoiceOCRTab tradePartner={{ info: { companyName: 'Dev Test Company' } }} />} />
            <Route path="/dev/financial-analyzer" element={<FinancialDocumentAnalyzer />} />
            <Route path="/halitrade-demo" element={<HaliTradeDemo />} />
            <Route path="/doc-agent" element={
              <ProtectedRoute>
                <PlatformLayout>
                  <HaliDocAgentPage />
                </PlatformLayout>
              </ProtectedRoute>
            } />
            <Route path="/doc-agent/:docRef" element={
              <ProtectedRoute>
                <PlatformLayout>
                  <HaliDocAgentPage />
                </PlatformLayout>
              </ProtectedRoute>
            } />
            <Route path="/confirm-invoice/:id" element={<TradePartnerConfirmation />} />

            {/* Protected Routes with Layouts - Platform Admin only */}
            <Route
              path="/factor-tools/*"
              element={
                <ProtectedRoute requiredRole="platform-admin">
                  <PlatformLayout>
                    <Routes>
                      <Route path="/" element={<PlatformFactorTools />} />
                      <Route path="invoice-onboarding" element={<HaliTradeInvoiceOnboarding embedded={false} />} />
                      <Route path="onboarding-studio" element={<OnboardingStudioHaliTools />} />
                      <Route path="financial-analysis" element={<FinancialAgent />} />
                      <Route path="financial-analysis/:recordId" element={<FinancialAgent />} />
                      <Route path="invoice-financing" element={<InvoiceFinancing />} />
                      <Route path="invoice-financing-check" element={<InvoiceFinancingCheck />} />
                      <Route path="trade-document-risk" element={<TradeDocumentRiskAnalyzerGate />} />
                      <Route path="trade-document-risk/:recordId" element={<TradeDocumentRiskAnalyzerGate />} />
                      <Route path="past-trade-studio" element={<PastTradeStudio />} />
                      <Route path="past-trade-studio/:recordId" element={<PastTradeStudio />} />
                      <Route path="contract-analyzer" element={<ContractAgent />} />
                      <Route path="contract-analyzer/:recordId" element={<ContractAgent />} />
                      <Route path="risk-analysis" element={<RiskAnalysis />} />
                      <Route path="financial-document-analyzer" element={<FinancialDocumentAnalyzer />} />
                      <Route path="bl-analyzer" element={<BLAnalyzer />} />
                      <Route path="invoice-risk-analyzer" element={<InvoiceRiskAnalyzer />} />
                      <Route path="agent-kyc" element={<AgentKYC />} />
                      <Route path="onboarding-invoice-new" element={<OnboardingStudioHaliTools />} />
                      <Route path="kyc-agent" element={<KYCAgentTest />} />
                      <Route path="financial-agent" element={<FactorToolsAgent />} />
                      <Route path="legal-officer" element={<LegalOfficer />} />
                      <Route path="legal-agent" element={<ContractAgent />} />
                      <Route path="legal-agent/:recordId" element={<ContractAgent />} />
                      <Route path="kyc-ai" element={<KycAIAgent />} />
                      <Route path="bank-statement" element={<BankStatementPage />} />
                    </Routes>
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/analytics/*"
              element={
                <ProtectedRoute>
                  <PlatformLayout>
                    <Routes>
                      <Route path="/" element={<AnalyticsHub />} />
                      <Route path="termsheets" element={<SalesTermsheet />} />
                      <Route path="credit-limit" element={<CreditLimitDashboard />} />
                      <Route path="financed" element={<FinancedDashboard />} />
                      <Route path="aum" element={<AumSalesProductDashboard />} />
                      <Route path="opportunities" element={<OpportunitiesDashboard />} />
                      <Route path="signup" element={<SignUpDashboard />} />
                    </Routes>
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/trade-partners/*"
              element={<Navigate to="/halitrade/trade-partners" replace />}
            />

            <Route
              path="/halitrade/*"
              element={
                <ProtectedRoute>
                  <HaliTradeEntry />
                </ProtectedRoute>
              }
            />

            <Route
              path="/haliflow/*"
              element={
                <ProtectedRoute>
                  <HaliFlowRouter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/haliagent/*"
              element={
                <ProtectedRoute>
                  <HaliAgentRoutes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/halilab/*"
              element={<HaliLabRoutes />}
            />

            <Route
              path="/investor/*"
              element={
                <ProtectedRoute>
                  <HaliFlowRouter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/halifund/*"
              element={
                <ProtectedRoute>
                  <HalifundRoutes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/buyer/*"
              element={
                <ProtectedRoute>
                  <BuyerRoutes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer/*"
              element={
                <ProtectedRoute>
                  <CustomerEntry />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/*"
              element={
                <ProtectedRoute requiredRole="platform-admin">
                  <PlatformRoutes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/referral/*"
              element={
                <ProtectedRoute>
                  <ReferralEntry />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </RoleProvider>
    </Router>
  );
};

export default AppRouter;
